import { useTranslation } from 'react-i18next';

// Custom hook for liquidation map translations
export const useLiquidationTranslations = () => {
  const { t } = useTranslation();
  
  return {
    // Common translations
    loading: t('common.loading'),
    error: t('common.error'),
    noData: t('common.noData'),
    
    // Liquidation specific translations
    title: t('liquidation.title'),
    subtitle: t('liquidation.subtitle'),
    currentPrice: t('liquidation.currentPrice'),
    liquidationPrice: t('liquidation.liquidationPrice'),
    liquidationAmount: t('liquidation.liquidationAmount'),
    priceRange: t('liquidation.priceRange'),
    totalLiquidationAmount: t('liquidation.totalLiquidationAmount'),
    minLiquidationPrice: t('liquidation.minLiquidationPrice'),
    maxLiquidationPrice: t('liquidation.maxLiquidationPrice'),
    totalPositions: t('liquidation.totalPositions'),
    riskBelowCurrent: t('liquidation.riskBelowCurrent'),
    riskAboveCurrent: t('liquidation.riskAboveCurrent'),
    riskDistribution: t('liquidation.riskDistribution'),
    leverageLevels: t('liquidation.leverageLevels'),
    selectedLeverageLevels: t('liquidation.selectedLeverageLevels'),
    viewOptions: t('liquidation.viewOptions'),
    viewMode: t('liquidation.viewMode'),
    bucketSize: t('liquidation.bucketSize'),
    individualPositions: t('liquidation.individualPositions'),
    densityView: t('liquidation.densityView'),
    aggregatedView: t('liquidation.aggregatedView'),
    riskAnalysis: t('liquidation.riskAnalysis'),
    priceRangeAnalysis: t('liquidation.priceRangeAnalysis'),
    high: t('liquidation.high'),
    low: t('liquidation.low'),
    below: t('liquidation.below'),
    above: t('liquidation.above'),
    ofCurrentPrice: t('liquidation.ofCurrentPrice'),
    
    // Leverage translations
    leverage: {
      '10x': t('liquidation.leverage.10x'),
      '25x': t('liquidation.leverage.25x'),
      '50x': t('liquidation.leverage.50x'),
      '100x': t('liquidation.leverage.100x'),
    },
  };
};

// Custom hook for navigation translations
export const useNavigationTranslations = () => {
  const { t } = useTranslation();
  
  return {
    home: t('navigation.home'),
    charts: t('navigation.charts'),
    price: t('navigation.price'),
    liquidation: t('navigation.liquidation'),
    about: t('navigation.about'),
    contact: t('navigation.contact'),
  };
};

// Custom hook for language switcher
export const useLanguageTranslations = () => {
  const { t } = useTranslation();
  
  return {
    switch: t('language.switch'),
    english: t('language.english'),
    chinese: t('language.chinese'),
  };
};

// Custom hook for report translations
export const useReportTranslations = () => {
  const { t } = useTranslation();
  
  return {
    title: t('report.title'),
    generateReport: t('report.generateReport'),
    generateAnalysisReport: t('report.generateAnalysisReport'),
    clickToGenerate: t('report.clickToGenerate'),
    enterInviteCode: t('report.enterInviteCode'),
    inviteCodeRequired: t('report.inviteCodeRequired'),
    inviteCodeDescription: t('report.inviteCodeDescription'),
    inviteCodeLabel: t('report.inviteCodeLabel'),
    inviteCodePlaceholder: t('report.inviteCodePlaceholder'),
    inviteCodeRequiredError: t('report.inviteCodeRequiredError'),
    inviteCodeMinLengthError: t('report.inviteCodeMinLengthError'),
    generatingReport: t('report.generatingReport'),
    failedToGetReport: t('report.failedToGetReport'),
    noReportData: t('report.noReportData'),
  };
};

// Custom hook for news report translations
export const useNewsReportTranslations = () => {
  const { t } = useTranslation();
  
  return {
    title: t('newsReport.title'),
    generateReport: t('newsReport.generateReport'),
    generateNewsReport: t('newsReport.generateNewsReport'),
    clickToGenerate: t('newsReport.clickToGenerate'),
    enterInviteCode: t('newsReport.enterInviteCode'),
    inviteCodeRequired: t('newsReport.inviteCodeRequired'),
    inviteCodeDescription: t('newsReport.inviteCodeDescription'),
    inviteCodeLabel: t('newsReport.inviteCodeLabel'),
    inviteCodePlaceholder: t('newsReport.inviteCodePlaceholder'),
    inviteCodeRequiredError: t('newsReport.inviteCodeRequiredError'),
    inviteCodeMinLengthError: t('newsReport.inviteCodeMinLengthError'),
    generatingReport: t('newsReport.generatingReport'),
    failedToGetReport: t('newsReport.failedToGetReport'),
    noReportData: t('newsReport.noReportData'),
  };
};

// Custom hook for landing page translations
export const useLandingTranslations = () => {
  const { t } = useTranslation();
  
  return {
    // Hero section
    heroTitle: t('landing.heroTitle'),
    heroSubtitle: t('landing.heroSubtitle'),
    getStartedFree: t('landing.getStartedFree'),
    viewDemo: t('landing.viewDemo'),
    
    // Stats section
    dataPointsAnalyzed: t('landing.dataPointsAnalyzed'),
    activeUsers: t('landing.activeUsers'),
    uptime: t('landing.uptime'),
    realTimeUpdates: t('landing.realTimeUpdates'),
    
    // Features section
    featuresTitle: t('landing.featuresTitle'),
    featuresSubtitle: t('landing.featuresSubtitle'),
    liquidationAnalysis: t('landing.liquidationAnalysis'),
    liquidationAnalysisDesc: t('landing.liquidationAnalysisDesc'),
    aiNewsAnalysis: t('landing.aiNewsAnalysis'),
    aiNewsAnalysisDesc: t('landing.aiNewsAnalysisDesc'),
    realTimeCharts: t('landing.realTimeCharts'),
    realTimeChartsDesc: t('landing.realTimeChartsDesc'),
    lightningFast: t('landing.lightningFast'),
    lightningFastDesc: t('landing.lightningFastDesc'),
    secureReliable: t('landing.secureReliable'),
    secureReliableDesc: t('landing.secureReliableDesc'),
    globalCoverage: t('landing.globalCoverage'),
    globalCoverageDesc: t('landing.globalCoverageDesc'),
    
    // Data showcase section
    dataVisualizationTitle: t('landing.dataVisualizationTitle'),
    dataVisualizationSubtitle: t('landing.dataVisualizationSubtitle'),
    liquidationHeatmapTitle: t('landing.liquidationHeatmapTitle'),
    liquidationHeatmapDesc: t('landing.liquidationHeatmapDesc'),
    liquidationHeatmapFeature1: t('landing.liquidationHeatmapFeature1'),
    liquidationHeatmapFeature2: t('landing.liquidationHeatmapFeature2'),
    liquidationHeatmapFeature3: t('landing.liquidationHeatmapFeature3'),
    liquidationHeatmapFeature4: t('landing.liquidationHeatmapFeature4'),
    interactiveDemo: t('landing.interactiveDemo'),
    interactiveDemoDesc: t('landing.interactiveDemoDesc'),
    tryLiveDemo: t('landing.tryLiveDemo'),
    
    // News preview section
    newsAnalysisTitle: t('landing.newsAnalysisTitle'),
    newsAnalysisSubtitle: t('landing.newsAnalysisSubtitle'),
    generateNewsReport: t('landing.generateNewsReport'),
    generateNewsReportDesc: t('landing.generateNewsReportDesc'),
    realTimeNewsTitle: t('landing.realTimeNewsTitle'),
    realTimeNewsDesc: t('landing.realTimeNewsDesc'),
    realTimeNewsFeature1: t('landing.realTimeNewsFeature1'),
    realTimeNewsFeature2: t('landing.realTimeNewsFeature2'),
    realTimeNewsFeature3: t('landing.realTimeNewsFeature3'),
    realTimeNewsFeature4: t('landing.realTimeNewsFeature4'),
    
    // CTA section
    ctaTitle: t('landing.ctaTitle'),
    ctaSubtitle: t('landing.ctaSubtitle'),
    startFreeTrial: t('landing.startFreeTrial'),
    contactSales: t('landing.contactSales'),
    
    // Additional features
    enterpriseApi: t('landing.enterpriseApi'),
    enterpriseApiDesc: t('landing.enterpriseApiDesc'),
    complianceAudit: t('landing.complianceAudit'),
    complianceAuditDesc: t('landing.complianceAuditDesc'),
    cloudInfrastructure: t('landing.cloudInfrastructure'),
    cloudInfrastructureDesc: t('landing.cloudInfrastructureDesc'),
    
    // Quantitative analysis section
    quantitativeMarketIntelligence: t('landing.quantitativeMarketIntelligence'),
    quantitativeMarketIntelligenceDesc: t('landing.quantitativeMarketIntelligenceDesc'),
    probabilisticAsymmetryDetection: t('landing.probabilisticAsymmetryDetection'),
    probabilisticAsymmetryDetectionDesc: t('landing.probabilisticAsymmetryDetectionDesc'),
    probabilisticAsymmetryFeature1: t('landing.probabilisticAsymmetryFeature1'),
    probabilisticAsymmetryFeature2: t('landing.probabilisticAsymmetryFeature2'),
    probabilisticAsymmetryFeature3: t('landing.probabilisticAsymmetryFeature3'),
    probabilisticAsymmetryFeature4: t('landing.probabilisticAsymmetryFeature4'),
    quantitativeStrategyEngine: t('landing.quantitativeStrategyEngine'),
    quantitativeStrategyEngineDesc: t('landing.quantitativeStrategyEngineDesc'),
    exploreQuantitativeTools: t('landing.exploreQuantitativeTools'),
    
    // CTA section additional keys
    readyToElevateTitle: t('landing.readyToElevateTitle'),
    readyToElevateSubtitle: t('landing.readyToElevateSubtitle'),
    expertDemo: t('landing.expertDemo'),
    personalizedWalkthrough: t('landing.personalizedWalkthrough'),
    secureIntegration: t('landing.secureIntegration'),
    apiSdkSupport: t('landing.apiSdkSupport'),
    dedicatedSupport: t('landing.dedicatedSupport'),
    technicalAssistance: t('landing.technicalAssistance'),
    scheduleExpertDemo: t('landing.scheduleExpertDemo'),
    contactSalesTeam: t('landing.contactSalesTeam'),
    
    // News report button
    generateNewsReportButton: t('landing.generateNewsReportButton'),
    aiNewsAnalysisReport: t('landing.aiNewsAnalysisReport'),
    clickToGenerateTooltip: t('landing.clickToGenerateTooltip'),
    
    // Institutional clients section
    trustedByLeadingInstitutions: t('landing.trustedByLeadingInstitutions'),
    platformServesDescription: t('landing.platformServesDescription'),
    assetsUnderManagement: t('landing.assetsUnderManagement'),
    globalExchanges: t('landing.globalExchanges'),
    typeIICertified: t('landing.typeIICertified'),
    supportMonitoring: t('landing.supportMonitoring'),
  };
};

// Custom hook for price page translations
export const usePriceTranslations = () => {
  const { t } = useTranslation();
  
  return {
    // Price plans
    quarterlyPlan: t('price.quarterlyPlan'),
    yearlyPlan: t('price.yearlyPlan'),
    quarterlyPrice: t('price.quarterlyPrice'),
    yearlyPrice: t('price.yearlyPrice'),
    quarterlyAverage: t('price.quarterlyAverage'),
    yearlyAverage: t('price.yearlyAverage'),
    quarterlyDesc: t('price.quarterlyDesc'),
    yearlyDesc: t('price.yearlyDesc'),
    paymentNote: t('price.paymentNote'),
    
    // Invite code
    inviteCodeLabel: t('price.inviteCodeLabel'),
    inviteCodePlaceholder: t('price.inviteCodePlaceholder'),
    inviteCodeDiscount: t('price.inviteCodeDiscount'),
    
    // Payment methods
    alipay: t('price.alipay'),
    wechatPay: t('price.wechatPay'),
    usdtPay: t('price.usdtPay'),
    
    // Payment modal
    paymentModalTitle: t('price.paymentModalTitle'),
    paymentModalDesc: t('price.paymentModalDesc'),
    paymentCompleteNote: t('price.paymentCompleteNote'),
    
    // USDT Payment
    usdtPaymentTitle: t('price.usdtPaymentTitle'),
    usdtPaymentAmount: t('price.usdtPaymentAmount'),
    usdtPaymentOrderId: t('price.usdtPaymentOrderId'),
    usdtPaymentWalletAddress: t('price.usdtPaymentWalletAddress'),
    usdtPaymentInstructions: t('price.usdtPaymentInstructions'),
    usdtPaymentWarning: t('price.usdtPaymentWarning'),
    usdtPaymentSuccess: t('price.usdtPaymentSuccess'),
    usdtPaymentFailed: t('price.usdtPaymentFailed'),
    usdtPaymentExpired: t('price.usdtPaymentExpired'),
    
    // Common
    loginRequired: t('price.loginRequired'),
    paymentFailed: t('price.paymentFailed'),
  };
};
